import { Select, SelectSize } from "@swivl-mono/ui";
import { useTimezoneSelect } from "react-timezone-select";

interface TimezoneFieldProps {
  value: string;
  onChange: (next: string) => void;
}

export const DEFAULT_TIMEZONE = "America/New_York";

/**
 * IANA timezone picker built on our own Select.
 *
 * `react-timezone-select` is used for its headless `useTimezoneSelect` hook
 * only — that gives the curated zone list and the "(GMT-4:00) Eastern Time"
 * labels, with offsets recomputed for the current date so they follow DST. Its
 * bundled component is deliberately not rendered: it ships react-select's own
 * styling, which doesn't follow our design tokens.
 */
export function TimezoneField({ value, onChange }: TimezoneFieldProps): React.ReactElement {
  const { options, parseTimezone } = useTimezoneSelect({ labelStyle: "original" });

  const selected = parseTimezone(value || DEFAULT_TIMEZONE);
  const items = options.map((o) => ({ label: o.label, value: o.value }));
  // The hook's list is a curated subset, so a zone saved from elsewhere (an
  // import, the raw-vars panel) can be absent. Carry it as its own option
  // rather than letting the field render as though nothing is set.
  if (!items.some((o) => o.value === selected.value)) {
    items.unshift({ label: selected.label, value: selected.value });
  }

  return (
    <Select
      size={SelectSize.MEDIUM}
      hideClear
      hasSearchInDropdown
      selectedValues={[selected.value]}
      options={items}
      onChange={(values) => {
        if (values[0] !== undefined) onChange(values[0]);
      }}
    />
  );
}
